import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { useEffect, useMemo } from "react";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import Button from "../../../components/Buttons/Button";
import {
  GLOBAL_REPORT_GOALS_ACHIEVED,
  GLOBAL_REPORT_GOALS_CONFIGURE_BUTTON,
  GLOBAL_REPORT_GOALS_CONFIGURE_PROMPT,
  GLOBAL_REPORT_GOALS_PROJECTION_ACTUAL,
  GLOBAL_REPORT_GOALS_PROJECTION_FORECAST,
  GLOBAL_REPORT_GOALS_PROJECTION_TITLE,
  GLOBAL_REPORT_GOALS_REMAINING,
  GLOBAL_REPORT_GOALS_STACKED_TITLE,
  GLOBAL_REPORT_GOALS_TITLE,
  GLOBAL_REPORT_STATS_NO_DATA,
  VALUE_NUMBER,
} from "../../../i18n/keysTranslations";
import LineChart from "../../../components/Charts/LineChart";
import { buildGlobalReportGoalsProgressDonutDataset } from "../utils/buildGlobalReportGoalsProgressDonutDataset";
import {
  buildGlobalReportGoalsProjectionDataset,
  extractGlobalReportGoalsHistoricalSeries,
} from "../utils/buildGlobalReportGoalsProjectionDataset";
import useGlobalReportGoalsAiProjection from "../hooks/useGlobalReportGoalsAiProjection";
import GlobalReportGoalsProgressBarChart from "./GlobalReportGoalsProgressBarChart";

const sectionSx = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const ChartSkeleton = ({ height = 280 }) => (
  <Skeleton variant="rounded" height={height} sx={{ borderRadius: 2 }} />
);

const GoalsSectionPlaceholder = ({
  message,
  onConfigureGoals,
  configureLabel,
}) => (
  <Box sx={{ position: "relative", width: "100%" }}>
    <ChartSkeleton height={420} />
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        px: 3,
        textAlign: "center",
      }}
    >
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420 }}>
        {message}
      </Typography>
      {onConfigureGoals ? (
        <Button color="primary" onClick={onConfigureGoals}>
          {configureLabel}
        </Button>
      ) : null}
    </Box>
  </Box>
);

const StatisticsSection = ({ title, subtitle, children }) => (
  <Box sx={sectionSx}>
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
    ) : null}
    {children}
  </Box>
);

const ChartBlock = ({ title, subtitle, children, sx }) => (
  <Box sx={{ mt: 3, ...sx }}>
    {title ? (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
    ) : null}
    {subtitle ? (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
    ) : null}
    {children}
  </Box>
);

const GlobalReportGoalsProgressSection = ({
  kpiSummary,
  campaigns = {},
  statistics = {},
  dateRange,
  breakdown = "weekly",
  isLoadingStatistics = false,
  isLoadingCampaigns = false,
  isStatisticsSettledForRange = false,
  isGoalsConfigured = false,
  isGoalsContentVisible = false,
  onConfigureGoals,
  onContentSettledChange,
  onForecastChange,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const canRenderGoalsContent = isGoalsConfigured && isGoalsContentVisible;

  const isLoading =
    canRenderGoalsContent &&
    (isLoadingCampaigns || isLoadingStatistics || !isStatisticsSettledForRange);

  const kpiLabel = useMemo(() => {
    const key = kpiSummary?.primaryKpi?.trim();
    return key ? t(key) : "";
  }, [kpiSummary?.primaryKpi, t]);

  const historicalSeries = useMemo(() => {
    if (!canRenderGoalsContent) {
      return {
        historicalDaily: [],
        hasHistoricalActivity: false,
        achievedInWindow: 0,
      };
    }

    return extractGlobalReportGoalsHistoricalSeries({
      campaigns,
      statistics,
      dispatch,
      startDateTime: dateRange?.startDateTime,
      endDateTime: dateRange?.endDateTime,
    });
  }, [
    campaigns,
    canRenderGoalsContent,
    dateRange?.endDateTime,
    dateRange?.startDateTime,
    dispatch,
    statistics,
  ]);

  const { forecastDailyResults, isProjectionLoading } =
    useGlobalReportGoalsAiProjection({
      historicalDaily: historicalSeries.historicalDaily,
      hasHistoricalActivity: historicalSeries.hasHistoricalActivity,
      startDateTime: dateRange?.startDateTime,
      endDateTime: dateRange?.endDateTime,
      isStatisticsSettledForRange,
      isLoadingStatistics,
      enabled: canRenderGoalsContent,
    });

  useEffect(() => {
    if (typeof onForecastChange === "function") {
      onForecastChange(forecastDailyResults);
    }
  }, [forecastDailyResults, onForecastChange]);

  const projectionDataset = useMemo(() => {
    if (!canRenderGoalsContent) {
      return { hasData: false, chartData: [], chartConfig: {} };
    }

    const built = buildGlobalReportGoalsProjectionDataset({
      campaigns,
      statistics,
      dispatch,
      breakdown,
      startDateTime: dateRange?.startDateTime,
      endDateTime: dateRange?.endDateTime,
      forecastDailyResults: isProjectionLoading
        ? undefined
        : forecastDailyResults,
    });

    return {
      ...built,
      chartConfig: {
        dailyActual: {
          ...built.chartConfig.dailyActual,
          label: t(GLOBAL_REPORT_GOALS_PROJECTION_ACTUAL),
        },
        dailyProjected: {
          ...built.chartConfig.dailyProjected,
          label: t(GLOBAL_REPORT_GOALS_PROJECTION_FORECAST),
        },
      },
    };
  }, [
    breakdown,
    campaigns,
    canRenderGoalsContent,
    dateRange?.endDateTime,
    dateRange?.startDateTime,
    dispatch,
    forecastDailyResults,
    isProjectionLoading,
    statistics,
    t,
  ]);

  const progressBarDataset = useMemo(() => {
    if (!canRenderGoalsContent) {
      return {
        hasData: false,
        achieved: 0,
        volume: 0,
        percent: 0,
        exceededBy: 0,
        chartConfig: {},
      };
    }

    const volume = Number(kpiSummary?.volume) || 0;
    const achieved = historicalSeries.achievedInWindow ?? 0;

    return buildGlobalReportGoalsProgressDonutDataset({
      achieved,
      volume,
    });
  }, [
    canRenderGoalsContent,
    historicalSeries.achievedInWindow,
    kpiSummary?.volume,
  ]);

  const progressSummaryText = useMemo(() => {
    if (!kpiLabel) {
      return null;
    }
    return `${t(VALUE_NUMBER, { value: progressBarDataset.achieved })} / ${t(VALUE_NUMBER, { value: progressBarDataset.volume })} · ${kpiLabel}`;
  }, [kpiLabel, progressBarDataset.achieved, progressBarDataset.volume, t]);

  useEffect(() => {
    if (typeof onContentSettledChange !== "function") {
      return undefined;
    }

    if (!canRenderGoalsContent) {
      onContentSettledChange(true);
      return;
    }

    onContentSettledChange(!isLoading && !isProjectionLoading);
  }, [
    canRenderGoalsContent,
    isLoading,
    isProjectionLoading,
    onContentSettledChange,
  ]);

  // Reset only on unmount — cleanup-on-deps pulsed false and re-triggered PDF prewarm.
  useEffect(() => {
    if (typeof onContentSettledChange !== "function") {
      return undefined;
    }

    return () => onContentSettledChange(false);
  }, [onContentSettledChange]);

  if (!canRenderGoalsContent) {
    if (isGoalsConfigured) {
      return null;
    }

    return (
      <StatisticsSection title={t(GLOBAL_REPORT_GOALS_TITLE)}>
        <GoalsSectionPlaceholder
          message={t(GLOBAL_REPORT_GOALS_CONFIGURE_PROMPT)}
          onConfigureGoals={onConfigureGoals}
          configureLabel={t(GLOBAL_REPORT_GOALS_CONFIGURE_BUTTON)}
        />
      </StatisticsSection>
    );
  }

  if (isLoading) {
    return (
      <StatisticsSection title={t(GLOBAL_REPORT_GOALS_TITLE)}>
        <ChartSkeleton height={120} />
        <Box sx={{ mt: 3 }}>
          <ChartSkeleton height={320} />
        </Box>
      </StatisticsSection>
    );
  }

  const hasProgressData = progressBarDataset.hasData;
  const hasProjectionData = projectionDataset.hasData;
  const showProjectionChart =
    hasProjectionData &&
    historicalSeries.hasHistoricalActivity &&
    !isProjectionLoading;

  return (
    <StatisticsSection title={t(GLOBAL_REPORT_GOALS_TITLE)}>
      <Box
        data-pdf-capture-id="global-report-goals-section-card"
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <ChartBlock title={t(GLOBAL_REPORT_GOALS_STACKED_TITLE)} sx={{ mt: 0 }}>
          {hasProgressData ? (
            <GlobalReportGoalsProgressBarChart
              chartConfig={{
                achieved: {
                  ...progressBarDataset.chartConfig.achieved,
                  label: t(GLOBAL_REPORT_GOALS_ACHIEVED),
                },
                remaining: {
                  ...progressBarDataset.chartConfig.remaining,
                  label: t(GLOBAL_REPORT_GOALS_REMAINING),
                },
              }}
              percent={progressBarDataset.percent}
              summaryText={progressSummaryText}
              exceededBy={progressBarDataset.exceededBy}
              volume={progressBarDataset.volume}
              achieved={progressBarDataset.achieved}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t(GLOBAL_REPORT_STATS_NO_DATA)}
            </Typography>
          )}
        </ChartBlock>

        <ChartBlock title={t(GLOBAL_REPORT_GOALS_PROJECTION_TITLE)}>
          {isProjectionLoading ? (
            <ChartSkeleton height={320} />
          ) : showProjectionChart ? (
            <LineChart
              stacked={false}
              hasYAxis
              hasCartesianGrid={false}
              sx={{
                p: 0,
                border: "none",
                boxShadow: "none",
                backgroundColor: "transparent",
              }}
              chartData={projectionDataset.chartData}
              chartConfig={{
                ...projectionDataset.chartConfig,
                dailyActual: {
                  ...projectionDataset.chartConfig.dailyActual,
                  valueFormatter: (value) => t(VALUE_NUMBER, { value }),
                },
                dailyProjected: {
                  ...projectionDataset.chartConfig.dailyProjected,
                  valueFormatter: (value) => t(VALUE_NUMBER, { value }),
                },
              }}
              height={320}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t(GLOBAL_REPORT_STATS_NO_DATA)}
            </Typography>
          )}
        </ChartBlock>
      </Box>
    </StatisticsSection>
  );
};

export default GlobalReportGoalsProgressSection;
